import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

/**
 * Generic CRUD Entity Interface
 * All entities should have at least an id
 */
export interface CrudEntity {
  id: number;
  [key: string]: unknown;
}

/**
 * Helper type for child relation query args
 */
interface ChildRelationArgs {
  resource: string;
  parentId: number;
  relation: string;
}

/**
 * Helper to create a unique cache key for child relations
 */
const getChildCacheKey = (
  resource: string,
  parentId: number,
  relation: string
) => `${resource}-${parentId}-${relation}`;

/**
 * Generic CRUD API for club resources
 * Provides getAll, get, create, update, delete operations
 *
 * Usage:
 * - Use the hooks with the resource name as parameter
 * - e.g., useFetchAllQuery('partners') to fetch all partners
 */
export const clubCrudApi = createApi({
  reducerPath: 'apiClubCrud',
  baseQuery: API.baseQuery,
  tagTypes: ['ClubCrudResource'],
  endpoints: (builder) => ({
    /**
     * Fetch all items of a resource
     */
    fetchAll: builder.query<CrudEntity[], string>({
      query: (resource) => `/crud/club/${resource}`,
      providesTags: (result, error, resource) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ClubCrudResource' as const,
                id: `${resource}-${id}`,
              })),
              { type: 'ClubCrudResource', id: `${resource}-LIST` },
            ]
          : [{ type: 'ClubCrudResource', id: `${resource}-LIST` }],
    }),

    /**
     * Fetch a single item by id
     */
    fetchOne: builder.query<CrudEntity, { resource: string; id: number }>({
      query: ({ resource, id }) => `/crud/club/${resource}/${id}`,
      providesTags: (result, error, { resource, id }) => [
        { type: 'ClubCrudResource', id: `${resource}-${id}` },
      ],
    }),

    /**
     * Create a new item
     */
    create: builder.mutation<
      CrudEntity,
      { resource: string; data: Partial<CrudEntity> }
    >({
      query: ({ resource, data }) => ({
        url: `/crud/club/${resource}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { resource }) => [
        { type: 'ClubCrudResource', id: `${resource}-LIST` },
      ],
      async onQueryStarted({ resource }, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          // Optimistically update the list cache
          dispatch(
            clubCrudApi.util.updateQueryData('fetchAll', resource, (draft) => {
              draft.push(newItem);
            })
          );
        } catch {}
      },
    }),

    /**
     * Update an existing item
     */
    update: builder.mutation<
      CrudEntity,
      { resource: string; data: CrudEntity }
    >({
      query: ({ resource, data }) => ({
        url: `/crud/club/${resource}/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { resource, data }) => [
        { type: 'ClubCrudResource', id: `${resource}-${data.id}` },
      ],
      async onQueryStarted({ resource, data }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedItem } = await queryFulfilled;
          // Optimistically update the list cache
          dispatch(
            clubCrudApi.util.updateQueryData('fetchAll', resource, (draft) => {
              const index = draft.findIndex((item) => item.id === data.id);
              if (index !== -1) {
                draft[index] = updatedItem;
              }
            })
          );
        } catch {}
      },
    }),

    /**
     * Delete an item
     */
    delete: builder.mutation<void, { resource: string; id: number }>({
      query: ({ resource, id }) => ({
        url: `/crud/club/${resource}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { resource, id }) => [
        { type: 'ClubCrudResource', id: `${resource}-${id}` },
        { type: 'ClubCrudResource', id: `${resource}-LIST` },
      ],
      async onQueryStarted({ resource, id }, { dispatch, queryFulfilled }) {
        // Optimistic update - remove from list before server response
        const patchResult = dispatch(
          clubCrudApi.util.updateQueryData('fetchAll', resource, (draft) => {
            const index = draft.findIndex((item) => item.id === id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          // Rollback on error
          patchResult.undo();
        }
      },
    }),

    /**
     * Delete multiple items
     */
    deleteMultiple: builder.mutation<void, { resource: string; ids: number[] }>(
      {
        query: ({ resource, ids }) => ({
          url: `/crud/club/${resource}/delete-multiple`,
          method: 'POST',
          body: { ids },
        }),
        invalidatesTags: (result, error, { resource, ids }) => [
          ...ids.map((id) => ({
            type: 'ClubCrudResource' as const,
            id: `${resource}-${id}`,
          })),
          { type: 'ClubCrudResource', id: `${resource}-LIST` },
        ],
        async onQueryStarted({ resource, ids }, { dispatch, queryFulfilled }) {
          // Optimistic update - remove from list before server response
          const patchResult = dispatch(
            clubCrudApi.util.updateQueryData('fetchAll', resource, (draft) => {
              return draft.filter((item) => !ids.includes(item.id));
            })
          );
          try {
            await queryFulfilled;
          } catch {
            // Rollback on error
            patchResult.undo();
          }
        },
      }
    ),

    /**
     * Upload a file to a specific attribute of a resource item
     * POST /crud/club/{resource}/{id}/upload/{attribute}
     * Returns the updated model
     */
    uploadFile: builder.mutation<
      CrudEntity,
      { resource: string; id: number; attribute: string; file: FormData }
    >({
      query: ({ resource, id, attribute, file }) => ({
        url: `/crud/club/${resource}/${id}/upload/${attribute}`,
        method: 'POST',
        body: file,
      }),
      async onQueryStarted({ resource, id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedItem } = await queryFulfilled;
          // Update the list cache with the new item data
          dispatch(
            clubCrudApi.util.updateQueryData('fetchAll', resource, (draft) => {
              const index = draft.findIndex((item) => item.id === id);
              if (index !== -1) {
                draft[index] = updatedItem;
              }
            })
          );
        } catch {}
      },
    }),

    /**
     * Delete a file from a specific attribute of a resource item
     * DELETE /crud/club/{resource}/{id}/upload/{attribute}
     * Returns the updated model (with attribute set to null)
     */
    deleteFile: builder.mutation<
      CrudEntity,
      { resource: string; id: number; attribute: string }
    >({
      query: ({ resource, id, attribute }) => ({
        url: `/crud/club/${resource}/${id}/upload/${attribute}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { resource, id, attribute },
        { dispatch, queryFulfilled }
      ) {
        // Optimistic update - set attribute to null before server response
        const patchResult = dispatch(
          clubCrudApi.util.updateQueryData('fetchAll', resource, (draft) => {
            const index = draft.findIndex((item) => item.id === id);
            if (index !== -1) {
              draft[index][attribute] = null;
            }
          })
        );
        try {
          const { data: updatedItem } = await queryFulfilled;
          // Update with actual server response
          dispatch(
            clubCrudApi.util.updateQueryData('fetchAll', resource, (draft) => {
              const index = draft.findIndex((item) => item.id === id);
              if (index !== -1) {
                draft[index] = updatedItem;
              }
            })
          );
        } catch {
          // Rollback on error
          patchResult.undo();
        }
      },
    }),

    // ==========================================
    // CHILD RELATION ENDPOINTS
    // For nested CRUD like Partner -> Features
    // ==========================================

    /**
     * Fetch all child items of a parent resource
     * GET /crud/club/{resource}/{parentId}/{relation}
     */
    fetchChildAll: builder.query<CrudEntity[], ChildRelationArgs>({
      query: ({ resource, parentId, relation }) =>
        `/crud/club/${resource}/${parentId}/${relation}`,
      providesTags: (result, error, { resource, parentId, relation }) => {
        const cacheKey = getChildCacheKey(resource, parentId, relation);
        return result
          ? [
              ...result.map(({ id }) => ({
                type: 'ClubCrudResource' as const,
                id: `${cacheKey}-${id}`,
              })),
              { type: 'ClubCrudResource', id: `${cacheKey}-LIST` },
            ]
          : [{ type: 'ClubCrudResource', id: `${cacheKey}-LIST` }];
      },
    }),

    /**
     * Create a child item
     * POST /crud/club/{resource}/{parentId}/{relation}
     */
    createChild: builder.mutation<
      CrudEntity,
      ChildRelationArgs & { data: Partial<CrudEntity> }
    >({
      query: ({ resource, parentId, relation, data }) => ({
        url: `/crud/club/${resource}/${parentId}/${relation}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { resource, parentId, relation }) => [
        {
          type: 'ClubCrudResource',
          id: `${getChildCacheKey(resource, parentId, relation)}-LIST`,
        },
      ],
      async onQueryStarted(
        { resource, parentId, relation },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data: newItem } = await queryFulfilled;
          dispatch(
            clubCrudApi.util.updateQueryData(
              'fetchChildAll',
              { resource, parentId, relation },
              (draft) => {
                draft.push(newItem);
              }
            )
          );
        } catch {}
      },
    }),

    /**
     * Update a child item
     * PUT /crud/club/{resource}/{parentId}/{relation}/{childId}
     */
    updateChild: builder.mutation<
      CrudEntity,
      ChildRelationArgs & { data: CrudEntity }
    >({
      query: ({ resource, parentId, relation, data }) => ({
        url: `/crud/club/${resource}/${parentId}/${relation}/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (
        result,
        error,
        { resource, parentId, relation, data }
      ) => [
        {
          type: 'ClubCrudResource',
          id: `${getChildCacheKey(resource, parentId, relation)}-${data.id}`,
        },
      ],
      async onQueryStarted(
        { resource, parentId, relation, data },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data: updatedItem } = await queryFulfilled;
          dispatch(
            clubCrudApi.util.updateQueryData(
              'fetchChildAll',
              { resource, parentId, relation },
              (draft) => {
                const index = draft.findIndex((item) => item.id === data.id);
                if (index !== -1) {
                  draft[index] = updatedItem;
                }
              }
            )
          );
        } catch {}
      },
    }),

    /**
     * Delete a child item
     * DELETE /crud/club/{resource}/{parentId}/{relation}/{childId}
     */
    deleteChild: builder.mutation<
      void,
      ChildRelationArgs & { childId: number }
    >({
      query: ({ resource, parentId, relation, childId }) => ({
        url: `/crud/club/${resource}/${parentId}/${relation}/${childId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (
        result,
        error,
        { resource, parentId, relation, childId }
      ) => [
        {
          type: 'ClubCrudResource',
          id: `${getChildCacheKey(resource, parentId, relation)}-${childId}`,
        },
        {
          type: 'ClubCrudResource',
          id: `${getChildCacheKey(resource, parentId, relation)}-LIST`,
        },
      ],
      async onQueryStarted(
        { resource, parentId, relation, childId },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          clubCrudApi.util.updateQueryData(
            'fetchChildAll',
            { resource, parentId, relation },
            (draft) => {
              const index = draft.findIndex((item) => item.id === childId);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    /**
     * Delete multiple child items
     * POST /crud/club/{resource}/{parentId}/{relation}/delete-multiple
     */
    deleteChildMultiple: builder.mutation<
      void,
      ChildRelationArgs & { ids: number[] }
    >({
      query: ({ resource, parentId, relation, ids }) => ({
        url: `/crud/club/${resource}/${parentId}/${relation}/delete-multiple`,
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: (
        result,
        error,
        { resource, parentId, relation, ids }
      ) => {
        const cacheKey = getChildCacheKey(resource, parentId, relation);
        return [
          ...ids.map((id) => ({
            type: 'ClubCrudResource' as const,
            id: `${cacheKey}-${id}`,
          })),
          { type: 'ClubCrudResource', id: `${cacheKey}-LIST` },
        ];
      },
      async onQueryStarted(
        { resource, parentId, relation, ids },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          clubCrudApi.util.updateQueryData(
            'fetchChildAll',
            { resource, parentId, relation },
            (draft) => {
              return draft.filter((item) => !ids.includes(item.id));
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    /**
     * Upload a file to a child item's attribute
     * POST /crud/club/{resource}/{parentId}/{relation}/{childId}/upload/{attribute}
     */
    uploadChildFile: builder.mutation<
      CrudEntity,
      ChildRelationArgs & { childId: number; attribute: string; file: FormData }
    >({
      query: ({ resource, parentId, relation, childId, attribute, file }) => ({
        url: `/crud/club/${resource}/${parentId}/${relation}/${childId}/upload/${attribute}`,
        method: 'POST',
        body: file,
      }),
      async onQueryStarted(
        { resource, parentId, relation, childId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data: updatedItem } = await queryFulfilled;
          dispatch(
            clubCrudApi.util.updateQueryData(
              'fetchChildAll',
              { resource, parentId, relation },
              (draft) => {
                const index = draft.findIndex((item) => item.id === childId);
                if (index !== -1) {
                  draft[index] = updatedItem;
                }
              }
            )
          );
        } catch {}
      },
    }),

    /**
     * Delete a file from a child item's attribute
     * DELETE /crud/club/{resource}/{parentId}/{relation}/{childId}/upload/{attribute}
     */
    deleteChildFile: builder.mutation<
      CrudEntity,
      ChildRelationArgs & { childId: number; attribute: string }
    >({
      query: ({ resource, parentId, relation, childId, attribute }) => ({
        url: `/crud/club/${resource}/${parentId}/${relation}/${childId}/upload/${attribute}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { resource, parentId, relation, childId, attribute },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          clubCrudApi.util.updateQueryData(
            'fetchChildAll',
            { resource, parentId, relation },
            (draft) => {
              const index = draft.findIndex((item) => item.id === childId);
              if (index !== -1) {
                draft[index][attribute] = null;
              }
            }
          )
        );
        try {
          const { data: updatedItem } = await queryFulfilled;
          dispatch(
            clubCrudApi.util.updateQueryData(
              'fetchChildAll',
              { resource, parentId, relation },
              (draft) => {
                const index = draft.findIndex((item) => item.id === childId);
                if (index !== -1) {
                  draft[index] = updatedItem;
                }
              }
            )
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useFetchAllQuery,
  useLazyFetchAllQuery,
  useFetchOneQuery,
  useLazyFetchOneQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  useDeleteMultipleMutation,
  useUploadFileMutation,
  useDeleteFileMutation,
  // Child relation hooks
  useFetchChildAllQuery,
  useLazyFetchChildAllQuery,
  useCreateChildMutation,
  useUpdateChildMutation,
  useDeleteChildMutation,
  useDeleteChildMultipleMutation,
  useUploadChildFileMutation,
  useDeleteChildFileMutation,
  reducerPath,
  reducer,
  middleware,
} = clubCrudApi;
